import React, { useState, useEffect, useMemo } from 'react';
import {
  Search,
  Star,
  ThumbsUp,
  ThumbsDown,
  Check,
  X,
  Flag,
  MessageSquare,
  Loader2,
} from 'lucide-react';
import { PageHeader } from '@/components/shared/PageHeader';
import { StatsCard } from '@/components/shared/StatsCard';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { Checkbox } from '@/components/ui/checkbox';
import { formatDistanceToNow } from 'date-fns';
import { toast } from 'sonner';
import {
  getAllReviewList,
  getReviewListByStatus,
  getReviewCount,
  getReviewDashboard,
  getReview,
  moderateReview,
  deleteReview,
} from '@/services/reviewsApi';

// overall_rating from API can be a weird numeric string — normalize to 0–5
const parseRating = (value) => {
  const n = Number(value);
  if (isNaN(n)) return 0;
  if (n > 5) return Math.min(5, Math.max(0, Math.round(n)));
  return Math.min(5, Math.max(0, n));
};

const getSubRating = (ratings, type) => {
  if (!Array.isArray(ratings)) return 0;
  const found = ratings.find(
    (r) => r.rating_type === type || r.rating_type?.includes(type)
  );
  return found?.rating ?? 0;
};

export const ReviewsPage = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);

  const [activeTab, setActiveTab] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedReview, setSelectedReview] = useState(null);
  const [selectedReviews, setSelectedReviews] = useState([]);
  const [adminReply, setAdminReply] = useState('');
  const [deleteItem, setDeleteItem] = useState(null);

  // Stats
  const [pendingCount, setPendingCount] = useState(0);
  const [approvedToday, setApprovedToday] = useState(0);
  const [rejectedThisWeek, setRejectedThisWeek] = useState(0);
  const [totalReviews, setTotalReviews] = useState(0);

  // ==================== FETCH LIST ====================
  const fetchReviews = async (tab = activeTab) => {
    try {
      setLoading(true);
      let res;
      if (tab === 'all') {
        res = await getAllReviewList();
      } else {
        res = await getReviewListByStatus(tab);
      }
      const list = res?.data || (Array.isArray(res) ? res : []);
      setReviews(list);
    } catch (error) {
      console.error(error);
      toast.error('Failed to load reviews');
      setReviews([]);
    } finally {
      setLoading(false);
    }
  };

  // ==================== FETCH STATS ====================
  const fetchStats = async () => {
    try {
      const [dashboardRes, pendingRes] = await Promise.all([
        getReviewDashboard(),
        getReviewCount('pending').catch(() => null),
      ]);

      setApprovedToday(dashboardRes?.approved_today ?? 0);
      setRejectedThisWeek(dashboardRes?.rejected_this_week ?? 0);
      setTotalReviews(dashboardRes?.total_reviews ?? 0);

      if (pendingRes?.count !== undefined) {
        setPendingCount(pendingRes.count);
      } else {
        // fallback: count from current list if available
        setPendingCount(
          reviews.filter((r) => r.status === 'pending').length
        );
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    fetchReviews(activeTab);
    setSelectedReviews([]);
  }, [activeTab]);

  // ==================== FILTER (search only — tab is server-side) ====================
  const filteredReviews = useMemo(() => {
    if (!searchTerm.trim()) return reviews;
    const q = searchTerm.toLowerCase();
    return reviews.filter(
      (r) =>
        r.username?.toLowerCase().includes(q) ||
        r.firm_name?.toLowerCase().includes(q) ||
        r.headline?.toLowerCase().includes(q) ||
        r.description?.toLowerCase().includes(q)
    );
  }, [reviews, searchTerm]);

  // ==================== MODERATE ====================
  const handleModerate = async (id, status, note = '') => {
    try {
      await moderateReview({ id, status, moderator_note: note });
      toast.success(`Review ${status}`);
      setSelectedReview(null);
      fetchReviews(activeTab);
      fetchStats();
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.message || 'Failed to update review');
    }
  };

  const handleApprove = (id) => handleModerate(id, 'approved');
  const handleReject = (id) => handleModerate(id, 'rejected');

  const handleBulkAction = async (action) => {
    const status = action === 'approve' ? 'approved' : 'rejected';
    try {
      await Promise.all(
        selectedReviews.map((id) =>
          moderateReview({ id, status, moderator_note: '' })
        )
      );
      toast.success(`${selectedReviews.length} reviews ${status}`);
      setSelectedReviews([]);
      fetchReviews(activeTab);
      fetchStats();
    } catch (error) {
      console.error(error);
      toast.error('Bulk action failed');
    }
  };

  // ==================== DETAIL ====================
  const openDetail = async (review) => {
    setSelectedReview(review);
    setAdminReply('');
    try {
      setDetailLoading(true);
      const res = await getReview(review.id);
      const data = res?.data || res;
      if (data) setSelectedReview(data);
    } catch (error) {
      console.error(error);
      // keep list item data
    } finally {
      setDetailLoading(false);
    }
  };

  // ==================== DELETE ====================
  const handleDelete = async () => {
    if (!deleteItem) return;
    try {
      await deleteReview(deleteItem.id);
      toast.success('Review deleted');
      setDeleteItem(null);
      setSelectedReview(null);
      fetchReviews(activeTab);
      fetchStats();
    } catch (error) {
      console.error(error);
      toast.error('Failed to delete review');
    }
  };

  // ==================== REPLY (via moderate note for now) ====================
  const handlePostReply = async () => {
    if (!selectedReview || !adminReply.trim()) return;
    try {
      await moderateReview({
        id: selectedReview.id,
        status: selectedReview.status || 'approved',
        moderator_note: adminReply.trim(),
      });
      toast.success('Reply / note saved');
      setAdminReply('');
      openDetail(selectedReview);
    } catch (error) {
      console.error(error);
      toast.error('Failed to post reply');
    }
  };

  const ratingValue = (review) => parseRating(review?.overall_rating);

  return (
    <div>
      <PageHeader
        title="Reviews"
        description="Manage user reviews and ratings"
        breadcrumbs={['Admin', 'Community', 'Reviews']}
      />

      {/* Stats */}
      <div className="grid gap-6 md:grid-cols-4 mb-6">
        <StatsCard
          title="Pending"
          value={pendingCount}
          icon={Star}
          iconBg="bg-amber-100 text-amber-600"
        />
        <StatsCard
          title="Approved Today"
          value={approvedToday}
          icon={Check}
          iconBg="bg-emerald-100 text-emerald-600"
        />
        <StatsCard
          title="Rejected This Week"
          value={rejectedThisWeek}
          icon={X}
          iconBg="bg-red-100 text-red-600"
        />
        <StatsCard
          title="Total Reviews"
          value={totalReviews}
          icon={MessageSquare}
          iconBg="bg-blue-100 text-blue-600"
        />
      </div>

      <Card className="rounded-xl border-slate-200 shadow-sm">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-6">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList>
                <TabsTrigger value="all">All</TabsTrigger>
                <TabsTrigger value="pending">
                  Pending
                  {pendingCount > 0 && (
                    <Badge
                      variant="destructive"
                      className="ml-2 h-5 px-1.5 text-xs bg-amber-500"
                    >
                      {pendingCount}
                    </Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger value="approved">Approved</TabsTrigger>
                <TabsTrigger value="rejected">Rejected</TabsTrigger>
                <TabsTrigger value="flagged">Flagged</TabsTrigger>
              </TabsList>
            </Tabs>

            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search reviews..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          {selectedReviews.length > 0 && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between">
              <span className="text-sm font-medium text-blue-900">
                {selectedReviews.length} review
                {selectedReviews.length > 1 ? 's' : ''} selected
              </span>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={() => handleBulkAction('approve')}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  <Check className="h-4 w-4 mr-1" />
                  Approve
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleBulkAction('reject')}
                  className="text-red-600 hover:text-red-700"
                >
                  <X className="h-4 w-4 mr-1" />
                  Reject
                </Button>
              </div>
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
            </div>
          ) : (
            <div className="space-y-4">
              {filteredReviews.map((review) => {
                const stars = ratingValue(review);
                return (
                  <div
                    key={review.id}
                    className="border border-slate-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-start gap-4">
                      <Checkbox
                        checked={selectedReviews.includes(review.id)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSelectedReviews([...selectedReviews, review.id]);
                          } else {
                            setSelectedReviews(
                              selectedReviews.filter((id) => id !== review.id)
                            );
                          }
                        }}
                      />

                      <Avatar className="h-12 w-12">
                        <AvatarFallback>
                          {review.username?.[0]?.toUpperCase() || '?'}
                        </AvatarFallback>
                      </Avatar>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="text-sm font-medium text-slate-900">
                              {review.username}
                            </p>
                            <span className="text-slate-400">•</span>
                            <div className="flex items-center gap-1">
                              {review.firm_logo && (
                                <img
                                  src={review.firm_logo}
                                  alt=""
                                  className="h-5 w-5 rounded"
                                />
                              )}
                              <span className="text-sm text-slate-700 font-medium">
                                {review.firm_name}
                              </span>
                            </div>
                          </div>
                          <StatusBadge status={review.status} />
                        </div>

                        <div className="flex items-center gap-3 mb-2 flex-wrap">
                          <div className="flex">
                            {[...Array(5)].map((_, i) => (
                              <Star
                                key={i}
                                className={`h-4 w-4 ${
                                  i < stars
                                    ? 'fill-amber-400 text-amber-400'
                                    : 'text-slate-300'
                                }`}
                              />
                            ))}
                          </div>
                          {Array.isArray(review.ratings) &&
                            review.ratings.map((r) => (
                              <Badge key={r.rating_type} variant="outline" className="text-xs">
                                {r.rating_type?.replace(/_/g, ' ')}: {r.rating}/5
                              </Badge>
                            ))}
                        </div>

                        <p className="text-sm font-medium text-slate-800 mb-1">
                          {review.headline}
                        </p>
                        {(review.description || review.headline) && (
                          <p className="text-sm text-slate-600 mb-2 line-clamp-2">
                            {review.description || review.headline}
                          </p>
                        )}

                        <div className="flex items-center gap-4 text-xs text-muted-foreground">
                          <span>
                            {review.created_at
                              ? formatDistanceToNow(new Date(review.created_at), {
                                  addSuffix: true,
                                })
                              : '-'}
                          </span>
                          <span className="flex items-center gap-1">
                            <ThumbsUp className="h-3 w-3" />
                            {review.helpful_count ?? 0}
                          </span>
                          <span className="flex items-center gap-1">
                            <ThumbsDown className="h-3 w-3" />
                            {review.not_helpful_count ?? 0}
                          </span>
                        </div>
                      </div>

                      <div className="flex flex-col gap-2">
                        {review.status === 'pending' && (
                          <>
                            <Button
                              size="sm"
                              onClick={() => handleApprove(review.id)}
                              className="bg-emerald-600 hover:bg-emerald-700"
                            >
                              <Check className="h-4 w-4 mr-1" />
                              Approve
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleReject(review.id)}
                            >
                              <X className="h-4 w-4 mr-1" />
                              Reject
                            </Button>
                          </>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openDetail(review)}
                        >
                          View Details
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}

              {filteredReviews.length === 0 && (
                <div className="text-center py-12">
                  <MessageSquare className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-600">No reviews found</p>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail Sheet */}
      <Sheet
        open={!!selectedReview}
        onOpenChange={(open) => {
          if (!open) setSelectedReview(null);
        }}
      >
        <SheetContent className="w-[500px] sm:w-[600px] overflow-y-auto">
          {selectedReview && (
            <>
              <SheetHeader>
                <SheetTitle>Review Details</SheetTitle>
              </SheetHeader>

              {detailLoading ? (
                <div className="flex justify-center py-16">
                  <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
                </div>
              ) : (
                <div className="mt-6 space-y-6">
                  <div className="flex items-center gap-3">
                    <Avatar className="h-12 w-12">
                      <AvatarFallback>
                        {selectedReview.username?.[0]?.toUpperCase() || '?'}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-medium text-slate-900">
                        {selectedReview.username}
                      </p>
                      <StatusBadge status={selectedReview.status} />
                    </div>
                  </div>

                  <Separator />

                  <div>
                    <p className="text-xs text-muted-foreground mb-2">FIRM</p>
                    <div className="flex items-center gap-2">
                      {selectedReview.firm_logo && (
                        <img
                          src={selectedReview.firm_logo}
                          alt=""
                          className="h-8 w-8 rounded"
                        />
                      )}
                      <span className="font-medium">{selectedReview.firm_name}</span>
                    </div>
                  </div>

                  <div>
                    <p className="text-xs text-muted-foreground mb-2">
                      OVERALL RATING
                    </p>
                    <div className="flex items-center gap-2">
                      <div className="flex">
                        {[...Array(5)].map((_, i) => (
                          <Star
                            key={i}
                            className={`h-5 w-5 ${
                              i < ratingValue(selectedReview)
                                ? 'fill-amber-400 text-amber-400'
                                : 'text-slate-300'
                            }`}
                          />
                        ))}
                      </div>
                      <span className="text-lg font-semibold">
                        {ratingValue(selectedReview)}/5
                      </span>
                    </div>
                  </div>

                  {Array.isArray(selectedReview.ratings) &&
                    selectedReview.ratings.length > 0 && (
                      <div>
                        <p className="text-xs text-muted-foreground mb-3">
                          SUB-RATINGS
                        </p>
                        <div className="space-y-2">
                          {selectedReview.ratings.map((r) => (
                            <div key={r.rating_type}>
                              <div className="flex justify-between text-sm mb-1">
                                <span className="capitalize">
                                  {r.rating_type?.replace(/_/g, ' ')}
                                </span>
                                <span className="font-medium">{r.rating}/5</span>
                              </div>
                              <Progress value={(r.rating || 0) * 20} className="h-2" />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                  <div>
                    <p className="text-xs text-muted-foreground mb-2">HEADLINE</p>
                    <p className="text-sm font-medium text-slate-900">
                      {selectedReview.headline || '—'}
                    </p>
                  </div>

                  <div>
                    <p className="text-xs text-muted-foreground mb-2">REVIEW</p>
                    <p className="text-sm text-slate-700 leading-relaxed">
                      {selectedReview.description || '—'}
                    </p>
                  </div>

                  <div className="flex gap-4">
                    <div className="flex items-center gap-2">
                      <ThumbsUp className="h-4 w-4 text-emerald-600" />
                      <span className="text-sm font-medium">
                        {selectedReview.helpful_count ?? 0} helpful
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <ThumbsDown className="h-4 w-4 text-red-600" />
                      <span className="text-sm font-medium">
                        {selectedReview.not_helpful_count ?? 0} not helpful
                      </span>
                    </div>
                  </div>

                  {selectedReview.moderator_note && (
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-xs font-semibold text-blue-900 mb-1">
                        Moderator Note
                      </p>
                      <p className="text-sm text-blue-800">
                        {selectedReview.moderator_note}
                      </p>
                    </div>
                  )}

                  <div>
                    <p className="text-xs text-muted-foreground mb-2">
                      POST MODERATOR NOTE
                    </p>
                    <Textarea
                      placeholder="Write a note..."
                      value={adminReply}
                      onChange={(e) => setAdminReply(e.target.value)}
                      rows={3}
                    />
                    <Button size="sm" className="mt-2" onClick={handlePostReply}>
                      Save Note
                    </Button>
                  </div>

                  <Separator />

                  <div className="flex gap-2 flex-wrap">
                    {selectedReview.status === 'pending' && (
                      <>
                        <Button
                          onClick={() => handleApprove(selectedReview.id)}
                          className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                        >
                          <Check className="h-4 w-4 mr-2" />
                          Approve
                        </Button>
                        <Button
                          onClick={() => handleReject(selectedReview.id)}
                          variant="outline"
                          className="flex-1"
                        >
                          <X className="h-4 w-4 mr-2" />
                          Reject
                        </Button>
                      </>
                    )}
                    <Button
                      variant="destructive"
                      onClick={() => setDeleteItem(selectedReview)}
                    >
                      Delete Review
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </SheetContent>
      </Sheet>

      <ConfirmDialog
        open={!!deleteItem}
        onOpenChange={() => setDeleteItem(null)}
        title="Delete Review"
        description={`Are you sure you want to delete this review by ${
          deleteItem?.username || 'this user'
        }? This action cannot be undone.`}
        confirmLabel="Delete"
        variant="destructive"
        onConfirm={handleDelete}
      />
    </div>
  );
};